import { useState, Suspense, useEffect } from "react";
import { RENTER_STATUS } from "../../../../../types/renter.type";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../../common/components/ui/loading/Loading";
import {
  TableFiltersState,
  TableFilters,
} from "../../../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import { formatDateAndTime } from "../../../../../common/helpers/date.helpers";
import { ICommission } from "../../../../../types/commission.type";
import { getAllActivationRenew } from "@/common/services/commission.api";

const STATUS_LABELS = {
  [RENTER_STATUS.Ativo]: "Ativo",
  [RENTER_STATUS.Incompleto]: "Incompleto",
  [RENTER_STATUS.PendentePagamento]: "Pendente de pagamento",
  [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para aprovação",
  [RENTER_STATUS.ReservaPendentePagamento]: "Reserva pendente de pagamento",
  [RENTER_STATUS.ReservaPaga]: "Reserva paga",
  [RENTER_STATUS.Finished]: "Finalizado",
  [RENTER_STATUS.Recision]: "Recisão",
  [RENTER_STATUS.Recall]: "Recolhimento",
  [RENTER_STATUS.WaitingRescision]: "Aguardando recisão",
  [RENTER_STATUS.Congelado]: "Congelado",
};

const TYPE_COMMISSION_LABELS: Record<string, string> = {
  activation: "Ativações",
  "activation-plus": "Ativações Plus",
  renew: "Renovações",
};

type CommissionProps = {
  commission: ICommission[];
};

function Renew(props: CommissionProps) {
  const commission = props.commission;
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [commissionRenew, setRenew] = useState<ICommission[]>([]);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const headers = {
    technicalConsultant: "Consultor",
    sdr: "SDR",
    renterName: "Nome",
    licensePlate: "Placa",
    // renterStatus: 'Status',
    dueDate: "Vencimento",
    activationDate: "Ativado em",
    currentContractStartDate: "Contrato",
    typeCommission: "Comissão",
  };

  const technicalConsultant = commission[0].technicalConsultant;
  useEffect(() => {
    async function loadCommissionRenew() {
      setIsLoading(true);
      try {
        const result = await getAllActivationRenew({
          page,
          itemsPerPage,
          technicalConsultant,
        });
        if (!result.ok) {
          notifyError("Erro ao carregar ativações Renew.");
          return;
        }
        setRenew(result.data);
        setData({ itemsPerPage, totalItems: result.totalItems });
        setSdrTotal([result.sdrTotal, result.consultantTotal]);
      } catch (err) {
        notifyError("Erro inesperado ao carregar Renew.");
      } finally {
        setIsLoading(false);
      }
    }
    loadCommissionRenew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicalConsultant, page]);

  const [sdrTotal, setSdrTotal] = useState([0, 0]);
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="tabs-border-line-commission">
        <div className="tabs-item-style-commission">
          <TableFilters
            dashboardHeader="Ativações Renew"
            placeholder="nome, email, cpf, cnh..."
            statusOptions={STATUS_LABELS}
            newItemLabel="Novo Cliente"
            onSearch={handleFilterChange}
            entity="commission"
            tableData={commission}
          />

          <div className="flex gap-4 mb-4">
            <div className="bg-white shadow-md rounded-lg p-4 w-full border border-gray-200">
              <h3 className="text-sm text-gray-600">Comissão SDR</h3>
              <p className="text-xl font-bold text-green-600">
                R$ {sdrTotal[0].toFixed(2)}
              </p>
            </div>
            <div className="bg-white shadow-md rounded-lg p-4 w-full border border-gray-200">
              <h3 className="text-sm text-gray-600">Comissão Consultor</h3>
              <p className="text-xl font-bold text-blue-600">
                R$ {sdrTotal[1].toFixed(2)}
              </p>
            </div>
          </div>

          <GenericTable
            headers={headers}
            data={commissionRenew}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                renterName: (name, commission) => (
                  <a
                    href={`/clientes/comissoes/${commission.renterId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/clientes/comissoes/${commission.renterId}`);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {name as string}
                  </a>
                ),
                licensePlate: (licensePlate, comission) => (
                  <a
                    href={`/veiculos/comissoes/${
                      (comission as ICommission).licensePlate
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/veiculos/comissoes/${
                          (comission as ICommission).licensePlate
                        }`
                      );
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {licensePlate as string}
                  </a>
                ),
                activationDate: formatDateAndTime,
                currentContractStartDate: formatDateAndTime,
                typeCommission: (type: string) =>
                  TYPE_COMMISSION_LABELS[type] || type,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity="commissions"
          />
          {isLoading && <Loading />}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 10)}
            goToPage={setPage}
          />
        </div>
      </div>
    </Suspense>
  );
}

export default Renew;
